/**
 * WebGL renderer sized to the viewport, on every device.
 *
 * Resizing is driven by ResizeObserver rather than window.resize because on iPad and
 * phone the canvas changes size when the URL bar collapses or the keyboard opens
 * without a window resize event firing.
 */
import * as THREE from 'three'

/**
 * Cap device pixel ratio at 2. Phones ship DPR 3+, and rendering a 3x buffer costs
 * ~2.25x the fragment work of 2x for no visible gain on a hand-held screen.
 */
const MAX_PIXEL_RATIO = 2

export function createRenderer(canvas: HTMLCanvasElement): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: 'high-performance',
  })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO))
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  // Realistic art direction: physical lighting response and filmic tonemapping,
  // not the flat linear look.
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.25
  renderer.outputColorSpace = THREE.SRGBColorSpace
  return renderer
}

/**
 * Keep renderer and camera matched to the canvas's real CSS size.
 * Returns a teardown function.
 */
export function bindResize(
  renderer: THREE.WebGLRenderer,
  camera: THREE.PerspectiveCamera,
  canvas: HTMLCanvasElement,
): () => void {
  const apply = (): void => {
    const width = canvas.clientWidth || window.innerWidth
    const height = canvas.clientHeight || window.innerHeight
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO))
    renderer.setSize(width, height, false)
    camera.aspect = width / height
    camera.updateProjectionMatrix()
  }

  apply()

  // Three overlapping triggers, because no single one covers every device:
  //   - ResizeObserver catches canvas-only changes (iPad URL bar collapsing, keyboard
  //     opening) that never fire a window resize. But its callbacks are delivered with
  //     the rendering steps, so a backgrounded tab won't run them.
  //   - window resize covers the ordinary desktop case and fires even when the
  //     rendering steps are throttled.
  //   - orientationchange fires when an iPad rotates, before the canvas settles.
  const observer = new ResizeObserver(apply)
  observer.observe(canvas)
  window.addEventListener('resize', apply)
  window.addEventListener('orientationchange', apply)

  return () => {
    observer.disconnect()
    window.removeEventListener('resize', apply)
    window.removeEventListener('orientationchange', apply)
  }
}
